<template>
  <transition name="fade">
    <b-card no-body class="w-100">
      <div slot="header" class="header">
        <div>Voyages (total: {{ totalItems }})</div>
        <div class="card-header-actions">
          <div v-if="exportTypes && csvAccess">
            <b-button
              v-for="(exportType, index) in exportTypes"
              @click="$emit('export', exportType)"
              :key="`type-${index}`"
              :disabled="disabled"
              variant="primary"
              size="sm"
              class="download-button">

              <template v-if="exportLoading">
                <i class="fa fa-refresh fa-spin"></i>
              </template>
              <template v-else>
                {{ exportType.toUpperCase() }}
              </template>

            </b-button>
          </div>
        </div>
      </div>

      <b-card-body class="card-content">
        <c-table
          class="h-100"
          :table-data="itemsFormatted"
          :fields="columns"
          :sorting="sorting"
          :striped="true"
          :bordered="false"
          :small="false"
          :perPage="10"
          :currentPageExternal="currentPage"
          @page-changed="pageChanged"
          caption="">
        </c-table>
      </b-card-body>
    </b-card>
  </transition>
</template>

<script>
import cTable from '../Table.vue'
import { mapActions } from 'vuex'
import moment from 'moment'
import { formatVolume } from '@/shared/number-utils';

const statusMap = {
  'L': 'Loading',
  'D': 'Discharging',
  'C': 'Complete',
  'T': 'In Transit',
  'WtD': 'Waiting to discharge',
  'WtL': 'Waiting to load',
  'U': 'Unknown'
}

export default {
  name: 'voyages',

  components: {
    cTable,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    items: {
      type: Array,
      default: () => []
    },

    currentPage: {
      type: Number,
      default: () => 1
    },

    sorting: {
      type: Boolean,
      default: false,
    },

    exportTypes: {
      type: Array,
      default: () => null
    },

    exportLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      columns: [
        {key: 'loadPortArrivedAt', sortable: true},
        {key: 'loadPortDepartedAt', sortable: true},
        {key: 'IMO', sortable: true},
        {key: 'DWT', sortable: true, class: 'text-right'},
        {key: 'vesselName', sortable: true},
        {key: 'parceling', sortable: true},
        {key: 'status', sortable: true},
        {key: 'loadCountry', sortable: true},
        {key: 'loadPortName', sortable: true},
        {key: 'loadBerthName', sortable: true},
        {key: 'dischargeCountry', sortable: true},
        {key: 'dischargePortName', sortable: true},
        {key: 'dischargePortArrivedAt', sortable: true},
        {key: 'segment', sortable: true},
        {key: 'volume', sortable: true, class: 'text-right'},
        {key: 'commodity', sortable: true},
      ],
      csvAccess: false
    };
  },

  async mounted() {
    await this.getUserData();
  },

  methods: {
    ...mapActions('api', ['getUser']),

    async getUserData() {
      let user = await this.getUser();
      this.csvAccess =
        user[
          'https://app.oceanbolt.com/user_authorization'
          ].permissions.includes('download:csv') || false;
    },

    pageChanged(currentPage, totalPages) {
      // we have to emit 2 events since we have 3 level in components: TradeFlows -> Voyages -> Table
      // data came from 'Tradeflows' so current/total page must be available there
      this.$emit('page-changed', currentPage, totalPages);
    }
  },

  computed: {
    itemsFormatted() {
      return this.items.map(item => {
        let formattedItem = {};
        this.columns.forEach(field => {
          if(field.key === 'IMO') {
            formattedItem[field.key] = item['imo'];
          } else if(field.key === 'DWT') {
            formattedItem[field.key] = formatVolume(item['dwt'], '1m');
          } else if(field.key === 'status') {
            formattedItem[field.key] = statusMap[item['status']];
          } else if(field.key === 'parceling') {
            formattedItem[field.key] = item[field.key] ? 'Parcel' : 'Single';
          } else if(field.key === 'dischargePortName') {
            formattedItem[field.key] = item[field.key] === 'Unknown Port' ? '-' : item[field.key];
          } else if(field.key === 'dischargeCountry') {
            formattedItem[field.key] = item[field.key] === 'Unknown Country' ? '-' : item[field.key];
          } else if(field.key === 'loadPortArrivedAt' || field.key === 'loadPortDepartedAt' || field.key === 'dischargePortArrivedAt') {
            formattedItem[field.key] = item[field.key] ? moment(item[field.key]).format('YYYY-MM-DD @ HH:mm') : '-';
          } else {
            formattedItem[field.key] = item[field.key];
          }
        });
        formattedItem.volume = formatVolume(formattedItem.volume, '1m');
        formattedItem.dwt = formatVolume(formattedItem.dwt, '1m');

        return formattedItem;

      });
    },

    totalItems() {
      return this.items.length;
    },
  },
};
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .card-header-actions {
    display: flex;
  }
}
</style>
